export type PurchaseFailureKind = "silent" | "pending" | "credit_later" | "failed";

export type PurchaseStage = "store" | "validation";

export type PurchaseFailureStage = "setup" | "store" | "validation" | "claim" | "restore";

const STAGE_KEY = Symbol.for( "bb.purchase_stage" );
const REPORTED_KEY = Symbol.for( "bb.purchase_reported" );

export const PURCHASE_FAILURE_TEXT_KEYS: Record<Exclude<PurchaseFailureKind, "silent">, string> = {
  pending: "PURCHASE.PENDING",
  credit_later: "PURCHASE.CREDIT_LATER",
  failed: "PURCHASE.FAILED",
};

const GOOGLE_BILLING_RESPONSES: Record<string, string> = {
  "-3": "service_timeout",
  "-2": "feature_not_supported",
  "-1": "service_disconnected",
  "1": "user_canceled",
  "2": "service_unavailable",
  "3": "billing_unavailable",
  "4": "item_unavailable",
  "5": "developer_error",
  "6": "error",
  "7": "item_already_owned",
  "8": "item_not_owned",
  "12": "network_error",
};

const ENVIRONMENT_BILLING_RESPONSES = new Set( [
  "billing_unavailable", "service_unavailable", "service_disconnected", "service_timeout", "network_error",
] );

const BILLING_CODE_MESSAGES = [
  "purchase_failed:",
  "purchase_cancelled",
  "launch_billing_flow_failed",
  "item_already_owned",
  "query_purchases_failed",
  "consume_purchase_failed",
  "acknowledge_purchase_failed",
];

const REASON_RULES: ReadonlyArray<readonly [ string, string ]> = [
  [ "cancel", "cancelled_by_user" ],
  [ "already_in_progress", "purchase_already_in_progress" ],
  [ "product_not_found", "product_not_found" ],
  [ "not configured for this platform", "product_not_configured" ],
  [ "not loaded", "offer_not_loaded" ],
  [ "not claimable", "offer_not_claimable" ],
  [ "must be claimed without a store purchase", "offer_not_purchasable" ],
  [ "no longer available", "offer_no_longer_available" ],
  [ "not ready to claim", "offer_no_longer_available" ],
  [ "billing_setup_failed", "billing_unavailable" ],
  [ "billing_client_unavailable", "billing_unavailable" ],
  [ "billing_service_disconnected", "billing_unavailable" ],
  [ "billing_setup_timeout", "billing_unavailable" ],
  [ "query_product_details_failed", "product_details_unavailable" ],
  [ "launch_billing_flow_failed", "billing_flow_not_started" ],
  [ "purchase_not_completed", "purchase_not_completed" ],
  [ "purchase_pending", "purchase_pending" ],
  [ "purchase_interrupted", "purchase_interrupted" ],
  [ "purchase_result_missing", "purchase_result_missing" ],
  [ "activity_unavailable", "activity_unavailable" ],
  [ "purchase_unverified", "store_verification_failed" ],
  [ "item_already_owned", "item_already_owned" ],
  [ "query_purchases_failed", "restore_query_failed" ],
  [ "consume_purchase_failed", "finish_failed" ],
  [ "acknowledge_purchase_failed", "finish_failed" ],
];

export function markPurchaseStage( error: unknown, stage: PurchaseStage ): void
{
  if ( error && typeof error === "object" )
    ( error as Record<symbol, unknown> )[STAGE_KEY] = stage;
}

export function purchaseStageOf( error: unknown ): PurchaseStage | null
{
  if ( !error || typeof error !== "object" ) return null;
  const stage = ( error as Record<symbol, unknown> )[STAGE_KEY];
  return stage === "store" || stage === "validation" ? stage : null;
}

export function markPurchaseErrorReported( error: unknown ): void
{
  if ( error && typeof error === "object" )
    ( error as Record<symbol, unknown> )[REPORTED_KEY] = true;
}

export function isPurchaseErrorReported( error: unknown ): boolean
{
  return !!error && typeof error === "object" && ( error as Record<symbol, unknown> )[REPORTED_KEY] === true;
}

export function purchaseFailureKind( error: unknown ): PurchaseFailureKind
{
  const message = ( error instanceof Error ? error.message : String( error ?? "" ) ).toLowerCase();
  if ( message.includes( "cancel" ) || message.includes( "purchase_interrupted" ) ) return "silent";
  if ( message.includes( "purchase_pending" ) || message.includes( "purchase_not_completed" ) ) return "pending";
  if ( purchaseStageOf( error ) === "validation" && isServerUnreachable( error ) ) return "credit_later";
  return "failed";
}

export function isPurchaseNetworkError( error: unknown ): boolean
{
  return error instanceof Error && error.name === "NetworkError";
}

export function purchaseHttpStatus( error: unknown ): number | null
{
  if ( !( error instanceof Error ) || error.name !== "ApiError" ) return null;
  const status = ( error as Error & { status?: unknown } ).status;
  return typeof status === "number" ? status : null;
}

export function purchaseFailureProperties( error: unknown, stage: PurchaseFailureStage ): Record<string, unknown>
{
  const message = error instanceof Error ? error.message : String( error ?? "unknown_error" );
  const raw = error && typeof error === "object" ? error as Record<string, unknown> : {};
  const code = typeof raw.code === "string" || typeof raw.code === "number" ? String( raw.code ) : undefined;
  const http_status = purchaseHttpStatus( error );
  const lower = message.toLowerCase();
  const billing_response = code !== undefined && BILLING_CODE_MESSAGES.some( prefix => lower.startsWith( prefix ) )
    ? GOOGLE_BILLING_RESPONSES[code] ?? `code_${code}`
    : undefined;

  return {
    purchase_stage: stage,
    failure_reason: failureReason( error, lower, http_status, billing_response ),
    ...( code !== undefined ? { failure_code: code } : {} ),
    ...( billing_response ? { billing_response } : {} ),
    ...( http_status !== null ? { http_status } : {} ),
    error_message: message.slice( 0, 500 ),
  };
}

export function shouldCapturePurchaseFailure( error: unknown, stage: PurchaseFailureStage ): boolean
{
  const kind = purchaseFailureKind( error );
  if ( kind === "silent" || kind === "pending" ) return false;
  if ( ( stage === "claim" || stage === "restore" ) && isPurchaseNetworkError( error ) ) return false;
  if ( stage === "store" || stage === "restore" )
  {
    const failure = purchaseFailureProperties( error, stage );
    if ( failure.failure_reason === "billing_unavailable" || ENVIRONMENT_BILLING_RESPONSES.has( String( failure.billing_response ?? "" ) ) )
      return false;
  }
  return true;
}

function failureReason( error: unknown, lower: string, http_status: number | null, billing_response: string | undefined ): string
{
  if ( http_status !== null ) return `validation_http_${http_status}`;
  if ( isPurchaseNetworkError( error ) ) return "network_error";
  for ( const [ needle, reason ] of REASON_RULES )
    if ( lower.includes( needle ) ) return reason;
  if ( lower.startsWith( "purchase_failed" ) ) return billing_response ? `store_error_${billing_response}` : "store_error";
  return "unknown";
}

function isServerUnreachable( error: unknown ): boolean
{
  if ( !( error instanceof Error ) ) return false;
  if ( error.name === "NetworkError" ) return true;
  const status = purchaseHttpStatus( error );
  return status !== null && status >= 500;
}
